import {
  CategoryOptionsResponseSchema,
  CreatePriceListBody,
  CreatePriceListBodySchema,
  CreatePriceListProductBody,
  CreatePriceListProductBodySchema,
  CreatePriceListProductParams,
  CreatePriceListProductResponse,
  CreatePriceListProductResponseSchema,
  CreatePriceListResponseSchema,
  DeletePriceListParams,
  DeletePriceListParamsSchema,
  DeletePriceListProductParams,
  PriceListImageGalleryParams,
  PriceListImageGalleryParamsSchema,
  PriceListImageGalleryResponseSchema,
  PriceListParams,
  PriceListParamsSchema,
  PriceListProductOptionsParams,
  PriceListProductOptionsParamsSchema,
  PriceListProductOptionsResponse,
  PriceListProductOptionsResponseSchema,
  PriceListProductsParams,
  PriceListProductsParamsSchema,
  PriceListProductsResponse,
  PriceListProductsResponseSchema,
  PriceListResponseSchema,
  TaxGroupSchema,
  TaxListParams,
  TaxListParamsSchema,
  TaxListSchema,
  UpdatePriceListBody,
  UpdatePriceListBodySchema,
  UpdatePriceListParams,
  UpdatePriceListParamsSchema,
  UpdatePriceListProductBody,
  UpdatePriceListProductBodySchema,
  UpdatePriceListProductParams,
  UpdateProductBody,
  UpdateProductBodySchema,
  UpdateProductParams,
  UpdateProductParamsSchema,
  UpdateProductResponse,
  UpdateProductResponseSchema,
  UploadImageBody,
  UploadImageBodySchema,
  UploadImageParams,
  UploadImageParamsSchema,
  UploadImageResponseSchema,
} from '@/api/_schemas/management'
import { apiBf as api } from '@/config/api-bf'
import { schematize } from '@/services/schematize'

async function createPriceList(body: CreatePriceListBody) {
  const parsedBody = schematize(body, CreatePriceListBodySchema)

  const { data } = await api.post('api/lista-preco', parsedBody)

  return schematize(data, CreatePriceListResponseSchema)
}

async function getPriceList(params: PriceListParams, signal?: AbortSignal) {
  const { page, search, status, orderDirection, orderField } = schematize(
    params,
    PriceListParamsSchema,
  )

  const { data } = await api.get('api/lista-preco', {
    params: {
      page,
      search,
      status,
      per_page: 20,
      order_direction: orderDirection,
      order_field: orderField,
    },
    signal,
  })

  return schematize(data, PriceListResponseSchema)
}

async function deletePriceList(params: DeletePriceListParams) {
  const { id } = schematize(params, DeletePriceListParamsSchema)

  return await api.delete(`api/lista-preco/${id}`)
}

async function updatePriceList(
  body: UpdatePriceListBody,
  params: UpdatePriceListParams,
) {
  const parsedBody = schematize(body, UpdatePriceListBodySchema)
  const { id } = schematize(params, UpdatePriceListParamsSchema)

  const { data } = await api.put(`api/lista-preco/${id}`, parsedBody)

  return schematize(data, CreatePriceListResponseSchema)
}

async function getPriceListProducts(params: PriceListProductsParams) {
  const { id } = schematize(params, PriceListProductsParamsSchema)

  const { data } = await api.get<PriceListProductsResponse>(
    `api/produto-lista-preco/${id}`,
  )

  return schematize(
    data.map((item) => ({
      ...item,
      produto: {
        ...item.produto,
        consumo: Boolean(item.produto.consumo),
        emitir_nfce: Boolean(item.produto.emitir_nfce),
        permitir_observacao: Boolean(item.produto.permitir_observacao),
        servico: Boolean(item.produto.servico),
        produto_combo_list:
          item.produto.produto_combo_list?.map((comboProduct) => ({
            ...comboProduct,
            produto: {
              ...comboProduct.produto,
              consumo: Boolean(comboProduct.produto.consumo),
              emitir_nfce: Boolean(comboProduct.produto.emitir_nfce),
              permitir_observacao: Boolean(
                comboProduct.produto.permitir_observacao,
              ),
              servico: Boolean(comboProduct.produto.servico),
            },
          })) ?? [],
      },
    })),
    PriceListProductsResponseSchema,
  )
}

async function createPriceListProduct(
  body: CreatePriceListProductBody,
  { id }: CreatePriceListProductParams,
) {
  const parsedBody = schematize(body, CreatePriceListProductBodySchema)

  const { data } = await api.post<CreatePriceListProductResponse>(
    `api/produto-lista-preco/${id}`,
    parsedBody,
  )

  return schematize(
    {
      ...data,
      produto: {
        ...data.produto,
        consumo: Boolean(data.produto.consumo),
        emitir_nfce: Boolean(data.produto.emitir_nfce),
        permitir_observacao: Boolean(data.produto.permitir_observacao),
        servico: Boolean(data.produto.servico),
      },
    },
    CreatePriceListProductResponseSchema,
  )
}

async function updatePriceListProduct(
  body: UpdatePriceListProductBody,
  { id }: UpdatePriceListProductParams,
) {
  const parsedBody = schematize(body, UpdatePriceListProductBodySchema)

  const { data } = await api.put<CreatePriceListProductResponse>(
    `api/produto-lista-preco/${id}`,
    parsedBody,
  )

  return schematize(
    {
      ...data,
      produto: {
        ...data.produto,
        consumo: Boolean(data.produto.consumo),
        emitir_nfce: Boolean(data.produto.emitir_nfce),
        permitir_observacao: Boolean(data.produto.permitir_observacao),
        servico: Boolean(data.produto.servico),
        produto_combo_list:
          data.produto.produto_combo_list?.map((comboProduct) => ({
            ...comboProduct,
            produto: {
              ...comboProduct.produto,
              consumo: Boolean(comboProduct.produto.consumo),
              emitir_nfce: Boolean(comboProduct.produto.emitir_nfce),
              permitir_observacao: Boolean(
                comboProduct.produto.permitir_observacao,
              ),
              servico: Boolean(comboProduct.produto.servico),
            },
          })) ?? [],
      },
    },
    CreatePriceListProductResponseSchema,
  )
}

async function deletePriceListProduct({ id }: DeletePriceListProductParams) {
  return await api.delete(`api/produto-lista-preco/${id}`)
}

async function updateProduct(
  body: UpdateProductBody,
  params: UpdateProductParams,
) {
  const parsedBody = schematize(body, UpdateProductBodySchema)
  const { id } = schematize(params, UpdateProductParamsSchema)

  const { data } = await api.put<UpdateProductResponse>(
    `api/produto/${id}`,
    parsedBody,
  )

  return schematize(
    {
      ...data,
      consumo: Boolean(data.consumo),
      emitir_nfce: Boolean(data.emitir_nfce),
      permitir_observacao: Boolean(data.permitir_observacao),
      servico: Boolean(data.servico),
      produto_combo_list:
        data.produto_combo_list?.map((comboProduct) => ({
          ...comboProduct,
          produto: {
            ...comboProduct.produto,
            consumo: Boolean(comboProduct.produto.consumo),
            emitir_nfce: Boolean(comboProduct.produto.emitir_nfce),
            permitir_observacao: Boolean(
              comboProduct.produto.permitir_observacao,
            ),
            servico: Boolean(comboProduct.produto.servico),
          },
        })) ?? [],
    },
    UpdateProductResponseSchema,
  )
}

async function getProduct(params: UpdateProductParams) {
  const { id } = schematize(params, UpdateProductParamsSchema)

  const { data } = await api.get<UpdateProductResponse>(`api/produto/${id}`)

  return schematize(
    {
      ...data,
      consumo: Boolean(data.consumo),
      emitir_nfce: Boolean(data.emitir_nfce),
      permitir_observacao: Boolean(data.permitir_observacao),
      servico: Boolean(data.servico),
      produto_combo_list:
        data.produto_combo_list?.map((comboProduct) => ({
          ...comboProduct,
          produto: {
            ...comboProduct.produto,
            consumo: Boolean(comboProduct.produto.consumo),
            emitir_nfce: Boolean(comboProduct.produto.emitir_nfce),
            permitir_observacao: Boolean(
              comboProduct.produto.permitir_observacao,
            ),
            servico: Boolean(comboProduct.produto.servico),
          },
        })) ?? [],
    },
    UpdateProductResponseSchema,
  )
}

async function getNcmList(params: TaxListParams) {
  const { search } = schematize(params, TaxListParamsSchema)

  const { data } = await api.get('api/produto-ncm-options-list', {
    params: { search },
  })

  return schematize(data, TaxListSchema)
}

async function getCfopList(params: TaxListParams) {
  const { search } = schematize(params, TaxListParamsSchema)

  const { data } = await api.get('api/produto-cfop-options-list', {
    params: { search },
  })

  return schematize(data, TaxListSchema)
}

async function getIcmsOrigemList() {
  const { data } = await api.get('api/produto-icms-origem-options-list')

  return schematize(data, TaxListSchema)
}

async function getIcmsSituacaoList() {
  const { data } = await api.get(
    'api/produto-icms-situacao-tributaria-options-list',
  )

  return schematize(data, TaxListSchema)
}

async function getIcmsModalidadeList() {
  const { data } = await api.get(
    'api/produto-icms-modalidade-base-calculo-options-list',
  )

  return schematize(data, TaxListSchema)
}

async function getPisCofinsTipoList() {
  const { data } = await api.get('api/produto-pis-cofins-tipo-tributacao-list')

  return schematize(data, TaxListSchema)
}

async function getPisCofinsSituacaoList() {
  const { data } = await api.get(
    'api/produto-situacao-tributaria-pis-cofins-options-list',
  )

  return schematize(data, TaxListSchema)
}

async function getTaxGroup(params: TaxListParams) {
  const { search } = schematize(params, TaxListParamsSchema)

  const { data } = await api.get('api/grupo-tributario-options-list', {
    params: { search },
  })

  return schematize(data, TaxGroupSchema)
}

async function getProductOptions(params: PriceListProductOptionsParams) {
  const { id } = schematize(params, PriceListProductOptionsParamsSchema)

  const { data } = await api.get<PriceListProductOptionsResponse>(
    `api/produto-lista-preco-produto-options-list/${id}`,
  )

  return schematize(
    data.map((produto) => ({
      ...produto,
      consumo: Boolean(produto.consumo),
      emitir_nfce: Boolean(produto.emitir_nfce),
      permitir_observacao: Boolean(produto.permitir_observacao),
      servico: Boolean(produto.servico),
      produto_combo_list:
        produto.produto_combo_list?.map((comboProduct) => ({
          ...comboProduct,
          produto: {
            ...comboProduct.produto,
            consumo: Boolean(comboProduct.produto.consumo),
            emitir_nfce: Boolean(comboProduct.produto.emitir_nfce),
            permitir_observacao: Boolean(
              comboProduct.produto.permitir_observacao,
            ),
            servico: Boolean(comboProduct.produto.servico),
          },
        })) ?? [],
    })),
    PriceListProductOptionsResponseSchema,
  )
}

async function getCategoryOptions() {
  const { data } = await api.get('api/categoria-options-list')

  return schematize(data, CategoryOptionsResponseSchema)
}

async function getImageGallery(
  params: PriceListImageGalleryParams,
  signal?: AbortSignal,
) {
  const { page, search } = schematize(params, PriceListImageGalleryParamsSchema)

  const { data } = await api.get('api/imagem', {
    params: { page, search, per_page: 25 },
    signal,
  })

  return schematize(data, PriceListImageGalleryResponseSchema)
}

async function uploadImage(body: UploadImageBody, params: UploadImageParams) {
  const parsedBody = schematize(body, UploadImageBodySchema)
  const { id } = schematize(params, UploadImageParamsSchema)

  const { data } = await api.post(`/api/imagem/${id}`, parsedBody)

  return schematize(
    {
      ...data,
      consumo: Boolean(data.consumo),
      emitir_nfce: Boolean(data.emitir_nfce),
      permitir_observacao: Boolean(data.permitir_observacao),
      servico: Boolean(data.servico),
    },
    UploadImageResponseSchema,
  )
}

export {
  createPriceList,
  deletePriceList,
  updatePriceList,
  getPriceList,
  getPriceListProducts,
  createPriceListProduct,
  updatePriceListProduct,
  deletePriceListProduct,
  updateProduct,
  getProduct,
  getNcmList,
  getCfopList,
  getIcmsOrigemList,
  getIcmsSituacaoList,
  getIcmsModalidadeList,
  getPisCofinsTipoList,
  getPisCofinsSituacaoList,
  getTaxGroup,
  getProductOptions,
  getCategoryOptions,
  getImageGallery,
  uploadImage,
}
